import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { toastStateSelector, usersAtom } from 'state';
import { useFormik } from 'formik';
import { ToastSeverity } from 'state';

import {
  Paper,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  FormHelperText,
  Chip,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material';

import { useTranslation } from 'react-i18next';

import { GeneralIds, ModelTypes, RolePermissions } from 'types/types';
import { Box } from '@mui/system';
import { getUserStatuses } from 'utils';
import { useState } from 'react';
import { Role, SubProject, User } from 'api/models';
import { getUserInitialValues, getUserValidationSchema } from 'forms';
import { query } from 'operations';
import { mutation } from 'operations/mutation';
import MuiPhoneNumber from 'mui-phone-number';
import { SaveButton } from 'components/SaveButton';
import ClearableSelect from 'components/ClearableSelect/ClearableSelect';
import { Permission, permissionState } from 'state/permissionState';
import { getPermissions, getSessionUser } from 'api';
import useRequest from 'hooks/useRequest';

export default function UserEditor() {
  const { userId, projectId } = useParams<GeneralIds>();
  const { t, i18n } = useTranslation();
  const setToast = useSetRecoilState(toastStateSelector);
  const statuses = getUserStatuses(t);
  const [roles, setRoles] = useState<Role[]>([]);
  const [subProjects, setSubProjects] = useState<SubProject[]>([]);
  const navigate = useNavigate();
  const [permissions, setPermissions] = useRecoilState(permissionState);
  const [user, setUser] = useState<User>();
  const isSessionUserSystemAdmin = permissions.includes(Permission.SUPERADMIN);

  const users = useRecoilValue(usersAtom);
  const { data: sessionUser } = useRequest<User>(getSessionUser, {
    dependencies: [window.location.pathname, users],
  });

  const fetchRoles = async (id?: string) => {
    const { data: roles } = await query<Role[]>({
      type: ModelTypes.role,
      ids: {
        id,
        projectId,
      },
    });

    const rolesWithoutSystemAdmin = roles.filter(
      role => !role.permissions.includes(RolePermissions.SuperAdmin),
    );

    setRoles(rolesWithoutSystemAdmin);
  };

  useRequest(async () => {
    const { data: subProjects } = await query<SubProject[]>({
      type: ModelTypes.subProject,
      ids: { projectId },
    });
    const { data: user } = await query<User>({
      type: ModelTypes.user,
      ids: { id: userId, projectId },
    });
    setUser(user);

    setSubProjects(subProjects);
    const subProjectId =
      (user?.subProjects?.length ?? 0) > 0
        ? user?.subProjects?.[0].id
        : subProjects?.[0].id;
    await fetchRoles(subProjectId);
  });

  const formik = useFormik({
    initialValues: getUserInitialValues(user),
    enableReinitialize: true,
    validationSchema: getUserValidationSchema(t),
    onSubmit: async values => {
      const {
        address,
        firstName,
        lastName,
        phone,
        status,
        roles,
        subProjectId,
        isSystemAdmin,
      } = values;

      try {
        await mutation({
          type: ModelTypes.user,
          body: {
            address,
            firstName,
            lastName,
            phone,
            roles,
            status,
            subProjectId,
            id: userId ?? '',
            isSystemAdmin,
          },
          ids: { id: userId, projectId },
        });

        const permissions = await getPermissions(projectId);
        setPermissions(permissions);

        setToast({
          isOpen: true,
          message: t('general.success.save'),
          severity: ToastSeverity.success,
        });
        navigate(-1);
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.save'),
          severity: ToastSeverity.error,
        });
      }
    },
  });

  return (
    <Paper
      sx={{
        padding: theme => theme.spacing(3),
        mt: theme => theme.spacing(3),
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid xs={12} md={6} item>
            <TextField
              fullWidth
              margin="normal"
              label={t('general.lastName')}
              type="text"
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
              inputProps={{ 'data-testid': 'user-editor-last-name' }}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <TextField
              fullWidth
              margin="normal"
              label={t('general.firstName')}
              type="text"
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
              inputProps={{
                'data-testid': 'user-editor-first-name',
              }}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <MuiPhoneNumber
              variant="outlined"
              fullWidth
              margin="normal"
              value={formik.values.phone}
              label={t('general.phone')}
              defaultCountry={'hu'}
              onChange={e => {
                // @ts-ignore
                formik.setFieldValue('phone', e?.replaceAll(' ', ''));
              }}
              name="phone"
              regions={'europe'}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
              inputProps={{
                'data-testid': 'user-editor-phone',
              }}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <TextField
              fullWidth
              margin="normal"
              label={t('general.address')}
              type="text"
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
              inputProps={{
                'data-testid': 'user-editor-address',
              }}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>{t('general.status')}</InputLabel>
              <Select
                name="status"
                value={formik.values.status}
                onChange={e => {
                  formik.handleChange(e);
                }}
                input={<OutlinedInput label={t('general.status')} />}
                error={formik.touched.status && Boolean(formik.errors.status)}
                renderValue={() => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {formik.values.status && (
                      <Chip
                        key={`status-select-chip-${formik.values.status}`}
                        label={t(`users.status.${formik.values.status}`)}
                      />
                    )}
                  </Box>
                )}
                inputProps={{
                  'data-testid': 'user-editor-status',
                }}
              >
                {statuses.map(status => (
                  <MenuItem
                    key={`status-select-option-${status.value}`}
                    value={status.value}
                  >
                    {t(`users.status.${status.value}`)}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.status && Boolean(formik.errors.status) && (
                <FormHelperText error={true}>
                  {t('general.errors.mandatoryField')}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid xs={12} md={6} item>
            <ClearableSelect
              fullWidth
              margin="normal"
              label={t('general.permissions') + ' *'}
              name="roles"
              multiple
              value={roles
                .map(role => {
                  if (formik.values.roles.find(r => r.id === role.id)) {
                    return role.id;
                  }
                  return null;
                })
                .filter(Boolean)}
              onChange={e => {
                const ids = e.target.value;
                const newRoles = roles.filter(role => ids.includes(role.id));
                formik.setFieldValue('roles', newRoles);
              }}
              error={formik.touched.roles && Boolean(formik.errors.roles)}
              helperText={
                formik.touched.roles &&
                Boolean(formik.errors.roles) &&
                t('general.errors.mandatoryField')
              }
              renderValue={() => {
                return formik.values.roles.map(role => (
                  <Chip
                    key={`user-editor-permission-select-chip-${role.id}`}
                    label={role.name}
                    sx={{ mr: 1 }}
                  />
                ));
              }}
              inputProps={{
                'data-testid': 'user-editor-roles',
              }}
            >
              {roles.map(role => (
                <MenuItem
                  key={`user-editor-permission-select-option-${role.id}`}
                  value={role.id}
                  id={role.id}
                >
                  <Checkbox
                    checked={
                      formik.values.roles.find(r => r.id === role.id) !==
                      undefined
                    }
                  />

                  {role.name}
                </MenuItem>
              ))}
            </ClearableSelect>
          </Grid>

          <Grid xs={12} md={6} item>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>{t('general.subProject')}</InputLabel>
              <Select
                name="subProjectId"
                value={formik.values.subProjectId}
                onChange={e => {
                  formik.handleChange(e);
                  fetchRoles(e.target.value);
                }}
                input={<OutlinedInput label={t('general.subProject')} />}
                error={
                  formik.touched.subProjectId &&
                  Boolean(formik.errors.subProjectId)
                }
                renderValue={() => {
                  const selectedSubProject = subProjects.find(
                    subProject => subProject.id === formik.values.subProjectId,
                  );
                  return (
                    <Chip
                      key={`subproject-select-chip-${formik.values.subProjectId}`}
                      label={selectedSubProject?.name}
                    />
                  );
                }}
                inputProps={{
                  'data-testid': 'user-editor-sub-project',
                }}
              >
                {subProjects.map(subProject => (
                  <MenuItem
                    key={`subproject-select-option-${formik.values.subProjectId}`}
                    value={subProject.id}
                  >
                    {subProject.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.subProjectId &&
                Boolean(formik.errors.subProjectId) && (
                  <FormHelperText error={true}>
                    {t('general.errors.mandatoryField')}
                  </FormHelperText>
                )}
            </FormControl>
          </Grid>
          {isSessionUserSystemAdmin && (
            <Grid xs={12} md={6} item alignItems={'center'} display={'flex'}>
              <FormControlLabel
                checked={formik.values.isSystemAdmin}
                disabled={sessionUser?.userId === user?.userId}
                name="isSystemAdmin"
                control={<Checkbox />}
                label={`${t('users.systemAdmin')}`}
                onChange={formik.handleChange}
              />
            </Grid>
          )}

          <Box display="flex" justifyContent="end">
            <SaveButton isLoading={formik.isSubmitting} />
          </Box>
        </Grid>
      </form>
    </Paper>
  );
}
